import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { seasonService } from '../../services/seasonService';
import {
  Plus,
  Search,
  Edit,
  Trash2
} from 'lucide-react';

const Season = () => {
  const [seasons, setSeasons] = useState([]);

  // Load seasons from API only
  const loadSeasons = async () => {
    try {
      const response = await seasonService.getSeasons();
      if (response.success) {
        const data = response.data || response;
        // Sort by ID descending to show newest items first
        const sortedData = Array.isArray(data) ? data.sort((a, b) => (b.id || 0) - (a.id || 0)) : data;
        setSeasons(sortedData);
      } else {
        throw new Error(response.message || 'Failed to load seasons');
      }
    } catch (error) {
      console.warn('Failed to load seasons from API:', error.message);
      setSeasons([]);
    }
  };

  useEffect(() => {
    loadSeasons();
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

  // Auto-generate code
  const generateCode = () => {
    if (!seasons || seasons.length === 0) return 'SE001';
    const maxCode = Math.max(...seasons.map(season => parseInt(season?.code?.replace('SE', '') || '0')), 0);
    const nextNumber = maxCode + 1;
    return `SE${nextNumber.toString().padStart(3, '0')}`;
  };

  const filteredSeasons = (seasons || []).filter(season => {
    // Safely handle undefined or null values
    const seasonCode = season?.code || '';
    const seasonName = season?.name || '';
    
    const matchesSearch = seasonCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         seasonName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || season.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleAdd = async (data) => {
    setIsLoading(true);
    try {
      const seasonData = {
        code: generateCode(), // Auto-generate code
        name: data.name,
        status: data.status || 'active'
      };

      const response = await seasonService.createSeason(seasonData);
      if (response.success) {
        await loadSeasons(); // Reload data from API
        toast.success('Season added successfully!');
        setShowAddModal(false);
        reset();
      } else {
        throw new Error(response.message || 'Failed to add season');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to add season');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setIsLoading(true);
    try {
      const seasonData = {
        code: editingSeason.code, // Keep existing code, don't allow editing
        name: data.name,
        status: data.status
      };

      const response = await seasonService.updateSeason(editingSeason.id, seasonData);
      if (response.success) {
        await loadSeasons(); // Reload data from API
        toast.success('Season updated successfully!');
        setShowAddModal(false);
        setEditingSeason(null);
        reset();
      } else {
        throw new Error(response.message || 'Failed to update season');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to update season');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (seasonId) => {
    if (!window.confirm('Are you sure you want to delete this season?')) return;

    setIsLoading(true);
    try {
      const response = await seasonService.deleteSeason(seasonId);
      if (response.success) {
        await loadSeasons(); // Reload data from API
        toast.success('Season deleted successfully!');
      } else {
        throw new Error(response.message || 'Failed to delete season');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to delete season');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (season) => {
    if (!season) return;
    setEditingSeason(season);
    setValue('code', season?.code || '');
    setValue('name', season?.name || '');
    setValue('status', season?.status || 'active');
    setShowAddModal(true);
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Season Management</h1>
          <p className="text-gray-600">Manage agricultural seasons for trials</p>
        </div>
        <button 
          onClick={() => {
            setEditingSeason(null);
            reset();
            // Pre-fill with auto-generated code
            setValue('code', generateCode());
            setShowAddModal(true);
          }}
          className="btn-primary flex items-center mt-4 sm:mt-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Season
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search seasons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Seasons Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Season Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSeasons.map((season) => (
                <tr key={season?.id || 'unknown'} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono font-semibold text-blue-600">{season?.code || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{season?.name || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(season?.status)}`}>
                      {season?.status || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(season)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(season?.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-700">
          Showing 1 to {filteredSeasons.length} of {filteredSeasons.length} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-gray-700">
            Page 1 of 1
          </span>
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingSeason ? 'Edit' : 'Add'} Season
              </h2>
              <form onSubmit={handleSubmit(editingSeason ? handleEdit : handleAdd)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    {...register('code', { required: 'Code is required' })}
                    className="input-field bg-gray-100 cursor-not-allowed"
                    placeholder="e.g., SE001, SE002"
                    maxLength={10}
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editingSeason ? 'Code cannot be edited' : 'Code will be generated automatically'}
                  </p>
                  {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Season Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Season name is required' })}
                    className="input-field"
                    placeholder="e.g., Kharif 2024, Rabi 2024-25"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    defaultValue="active"
                    {...register('status', { required: 'Status is required' })}
                    className="input-field"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingSeason(null);
                      reset();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : (editingSeason ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Season; 